import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    TextField,
    Alert,
    CircularProgress,
    Chip,
    Divider,
    Paper,
    Tab,
    Tabs,
} from '@mui/material';
import {
    Close as CloseIcon,
    CloudUpload as UploadIcon,
    Delete as DeleteIcon,
    Download as DownloadIcon,
    Link as LinkIcon,
    InsertDriveFile as FileIcon,
    LinkedIn as LinkedInIcon,
    Description as CVIcon,
    AttachFile as AdditionalIcon,
} from '@mui/icons-material';
import { useAppTheme } from '../../../App';
import { DocumentService } from '../services/documentService';
import { UserDocument, FILE_TYPE_LABELS } from '../types/document.types';
import { PRIMARY_ORANGE } from '../../../common/colors';
import { useSubscription } from '../../../hooks/useSubscription';

interface DocumentManagerDialogProps {
    open: boolean;
    onClose: () => void;
}

type TabValue = 'upload' | 'view';

const DocumentManagerDialog: React.FC<DocumentManagerDialogProps> = ({
    open,
    onClose,
}) => {
    const { isDarkMode, getThemeStyles } = useAppTheme();
    const theme = getThemeStyles();
    const { isPro } = useSubscription();

    const [currentTab, setCurrentTab] = useState<TabValue>('view');
    const [documents, setDocuments] = useState<UserDocument[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [notification, setNotification] = useState<{
        type: 'success' | 'error';
        message: string;
    } | null>(null);

    // Upload form states
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [linkedinUrl, setLinkedinUrl] = useState('');
    const [uploadType, setUploadType] = useState<'cv' | 'linkedin' | 'additional'>('cv');

    // Load documents when dialog opens
    useEffect(() => {
        if (open) {
            loadDocuments();
        }
    }, [open]);

    const loadDocuments = async () => {
        setIsLoading(true);
        try {
            const response = await DocumentService.getUserDocuments();
            if (response.status === 'success') {
                setDocuments(response.documents);
            } else {
                setNotification({
                    type: 'error',
                    message: response.message
                });
            }
        } catch (error) {
            console.log(error);
            setNotification({
                type: 'error',
                message: 'Failed to load documents'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const handleUpload = async () => {
        setIsUploading(true);
        setNotification(null);

        try {
            // Check document limits before upload
            if (uploadType === 'additional' && additionalCount >= additionalDocumentsLimit) {
                setNotification({
                    type: 'error',
                    message: `You've reached the maximum limit of ${additionalDocumentsLimit} additional documents${!isPro ? '. Upgrade to Pro to upload up to 10 additional documents.' : '.'}`
                });
                setIsUploading(false);
                return;
            }

            if (uploadType === 'cv' && cvCount >= 1) {
                setNotification({
                    type: 'error',
                    message: 'You can only upload 1 CV document. Please delete the existing CV to upload a new one.'
                });
                setIsUploading(false);
                return;
            }

            if (uploadType === 'linkedin' && linkedinCount >= 1) {
                setNotification({
                    type: 'error',
                    message: 'You can only add 1 LinkedIn profile. Please delete the existing profile to add a new one.'
                });
                setIsUploading(false);
                return;
            }

            let request;

            if (uploadType === 'linkedin') {
                if (!linkedinUrl.trim()) {
                    setNotification({
                        type: 'error',
                        message: 'Please enter your LinkedIn profile URL'
                    });
                    setIsUploading(false);
                    return;
                }

                request = {
                    fileType: uploadType,
                    linkUrl: linkedinUrl.trim()
                };
            } else {
                if (!selectedFile) {
                    setNotification({
                        type: 'error',
                        message: 'Please select a file to upload'
                    });
                    setIsUploading(false);
                    return;
                }

                request = {
                    fileType: uploadType,
                    file: selectedFile
                };
            }

            const response = await DocumentService.uploadDocument(request, {
                additionalDocumentsLimit
            });

            if (response.status === 'success') {
                setNotification({
                    type: 'success',
                    message: response.message
                });

                // Reset form
                setSelectedFile(null);
                setLinkedinUrl('');

                // Reload documents
                await loadDocuments();

                // Switch to view tab
                setCurrentTab('view');
            } else {
                setNotification({
                    type: 'error',
                    message: response.message
                });
            }
        } catch (error) {
            setNotification({
                type: 'error',
                message: 'Failed to upload document'
            });
        } finally {
            setIsUploading(false);
        }
    };

    const handleDelete = async (documentId: string, storagePath?: string) => {
        try {
            const response = await DocumentService.deleteDocument(documentId, storagePath);

            if (response.status === 'success') {
                setNotification({
                    type: 'success',
                    message: response.message
                });

                // Reload documents
                await loadDocuments();
            } else {
                setNotification({
                    type: 'error',
                    message: response.message
                });
            }
        } catch (error) {
            setNotification({
                type: 'error',
                message: 'Failed to delete document'
            });
        }
    };

    const getDocumentIcon = (fileType: string) => {
        switch (fileType) {
            case 'cv':
                return <CVIcon sx={{ color: PRIMARY_ORANGE.main }} />;
            case 'linkedin':
                return <LinkedInIcon sx={{ color: '#0077b5' }} />;
            case 'additional':
                return <AdditionalIcon sx={{ color: '#10b981' }} />;
            default:
                return <FileIcon sx={{ color: theme.textSecondary }} />;
        }
    };

    const getDocumentStats = () => {
        const cvCount = documents.filter(doc => doc.fileType === 'cv').length;
        const linkedinCount = documents.filter(doc => doc.fileType === 'linkedin').length;
        const additionalCount = documents.filter(doc => doc.fileType === 'additional').length;

        return { cvCount, linkedinCount, additionalCount };
    };

    const { cvCount, linkedinCount, additionalCount } = getDocumentStats();
    const additionalDocumentsLimit = isPro ? 10 : 3;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: { xs: 0, sm: 3 },
                    background: isDarkMode ? '#2d2d2d' : '#ffffff',
                    border: { xs: 'none', sm: `1px solid ${theme.borderColor}` },
                    minHeight: { xs: '100vh', sm: '600px' },
                    maxHeight: { xs: '100vh', sm: '80vh' },
                    margin: { xs: 0, sm: 'auto' },
                    width: { xs: '100%', sm: '90%', md: '80%', lg: '70%' },
                    maxWidth: { xs: '100%', sm: '1200px' },
                }
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 1,
                    px: { xs: 2, sm: 3 },
                    borderBottom: `1px solid ${theme.borderColor}`,
                    background: isDarkMode ? '#2d2d2d' : '#ffffff',
                }}
            >
                <Box>
                    <Typography variant="h6" sx={{ color: theme.textPrimary, fontWeight: 600 }}>
                        Document Manager
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.textSecondary, mt: 0.5 }}>
                        Manage your Global Talent Visa application documents
                    </Typography>
                </Box>
                <IconButton
                    onClick={onClose}
                    sx={{
                        color: theme.textSecondary,
                        '&:hover': {
                            background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                            color: theme.textPrimary,
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0, background: isDarkMode ? '#2d2d2d' : '#ffffff' }}>
                <Box sx={{ borderBottom: 1, borderColor: theme.borderColor }}>
                    <Tabs
                        value={currentTab}
                        onChange={(_, newValue) => {
                            // Prevent switching if upload is in progress
                            if (isUploading) return;
                            setCurrentTab(newValue);
                        }}
                        sx={{
                            px: { xs: 2, sm: 3 },
                            '& .MuiTab-root': {
                                color: theme.textSecondary,
                                fontWeight: 500,
                                textTransform: 'none',
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                minHeight: { xs: 48, sm: 56 },
                                '&:hover': {
                                    color: theme.textPrimary,
                                }
                            },
                            '& .Mui-selected': {
                                color: `${PRIMARY_ORANGE.main} !important`,
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: PRIMARY_ORANGE.main,
                            }
                        }}
                    >
                        <Tab label="View Documents" value="view" />
                        <Tab label="Upload Documents" value="upload" disabled={isUploading} />
                    </Tabs>
                </Box>

                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                    {notification && (
                        <Alert
                            severity={notification.type}
                            onClose={() => setNotification(null)}
                            sx={{
                                mb: 3,
                                borderRadius: 2,
                                backgroundColor: notification.type === 'success'
                                    ? (isDarkMode ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.1)')
                                    : (isDarkMode ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.1)'),
                                color: theme.textPrimary,
                                '& .MuiAlert-icon': {
                                    color: notification.type === 'success' ? '#10b981' : '#ef4444'
                                },
                                '& .MuiIconButton-root': {
                                    color: theme.textSecondary,
                                }
                            }}
                        >
                            {notification.message}
                        </Alert>
                    )}

                    {currentTab === 'view' && (
                        <Box>
                            {/* Document Stats */}
                            <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, mb: 3, flexWrap: 'wrap' }}>
                                <Chip
                                    icon={<CVIcon />}
                                    label={`CV: ${cvCount}/1`}
                                    color={cvCount > 0 ? 'success' : 'default'}
                                    variant={cvCount > 0 ? 'filled' : 'outlined'}
                                    sx={{
                                        color: cvCount > 0 ? '#ffffff' : theme.textPrimary,
                                        borderColor: cvCount > 0 ? '#10b981' : theme.borderColor,
                                        backgroundColor: cvCount > 0 ? '#10b981' : 'transparent',
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        height: { xs: 28, sm: 32 },
                                        '& .MuiChip-icon': {
                                            color: cvCount > 0 ? '#ffffff' : theme.textSecondary,
                                            fontSize: { xs: 16, sm: 18 }
                                        }
                                    }}
                                />
                                <Chip
                                    icon={<LinkedInIcon />}
                                    label={`LinkedIn: ${linkedinCount}/1`}
                                    color={linkedinCount > 0 ? 'success' : 'default'}
                                    variant={linkedinCount > 0 ? 'filled' : 'outlined'}
                                    sx={{
                                        color: linkedinCount > 0 ? '#ffffff' : theme.textPrimary,
                                        borderColor: linkedinCount > 0 ? '#10b981' : theme.borderColor,
                                        backgroundColor: linkedinCount > 0 ? '#10b981' : 'transparent',
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        height: { xs: 28, sm: 32 },
                                        '& .MuiChip-icon': {
                                            color: linkedinCount > 0 ? '#ffffff' : theme.textSecondary,
                                            fontSize: { xs: 16, sm: 18 }
                                        }
                                    }}
                                />
                                <Chip
                                    icon={<AdditionalIcon />}
                                    label={`Additional: ${additionalCount}/${additionalDocumentsLimit}`}
                                    color={additionalCount > 0 ? 'success' : 'default'}
                                    variant={additionalCount > 0 ? 'filled' : 'outlined'}
                                    sx={{
                                        color: additionalCount > 0 ? '#ffffff' : theme.textPrimary,
                                        borderColor: additionalCount > 0 ? '#10b981' : theme.borderColor,
                                        backgroundColor: additionalCount > 0 ? '#10b981' : 'transparent',
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        height: { xs: 28, sm: 32 },
                                        '& .MuiChip-icon': {
                                            color: additionalCount > 0 ? '#ffffff' : theme.textSecondary,
                                            fontSize: { xs: 16, sm: 18 }
                                        }
                                    }}
                                />
                            </Box>

                            {isLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress sx={{ color: PRIMARY_ORANGE.main }} />
                                </Box>
                            ) : documents.length === 0 ? (
                                <Paper
                                    sx={{
                                        p: 4,
                                        textAlign: 'center',
                                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                                        border: `1px solid ${theme.borderColor}`,
                                        borderRadius: 2,
                                    }}
                                >
                                    <FileIcon sx={{ fontSize: 48, color: theme.textSecondary, mb: 2 }} />
                                    <Typography variant="h6" sx={{ color: theme.textSecondary, mb: 1 }}>
                                        No documents uploaded yet
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.textSecondary }}>
                                        Upload your CV, LinkedIn profile, and supporting documents to get started
                                    </Typography>
                                </Paper>
                            ) : (
                                <List sx={{ '& .MuiListItem-root': { borderRadius: 2, mb: 1 } }}>
                                    {documents.map((document) => (
                                        <ListItem
                                            key={document.id}
                                            sx={{
                                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                                                border: `1px solid ${theme.borderColor}`,
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                alignItems: { xs: 'stretch', sm: 'center' },
                                                gap: { xs: 1, sm: 0 },
                                                '&:hover': {
                                                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                                                }
                                            }}
                                        >
                                            <ListItemIcon>
                                                {getDocumentIcon(document.fileType)}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography sx={{ color: theme.textPrimary, fontWeight: 500 }}>
                                                            {document.fileName}
                                                        </Typography>
                                                        <Chip
                                                            label={FILE_TYPE_LABELS[document.fileType]}
                                                            size="small"
                                                            sx={{
                                                                fontSize: '0.75rem',
                                                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                                                                color: theme.textSecondary,
                                                                border: `1px solid ${theme.borderColor}`,
                                                            }}
                                                        />
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box component="span" sx={{ mt: 0.5 }}>
                                                        <Typography
                                                            component="span"
                                                            variant="body2"
                                                            sx={{ color: theme.textSecondary }}
                                                        >
                                                            Uploaded {document.uploadedAt.toLocaleDateString()}
                                                            {document.fileSize && ` • ${DocumentService.formatFileSize(document.fileSize)}`}
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                            <ListItemSecondaryAction sx={{ position: { xs: 'static', sm: 'absolute' }, right: { xs: 'auto', sm: 16 } }}>
                                                <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
                                                    {(document.fileUrl || document.linkUrl) && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                const url = document.fileUrl || document.linkUrl;
                                                                if (url) window.open(url, '_blank');
                                                            }}
                                                            sx={{
                                                                color: theme.textSecondary,
                                                                '&:hover': {
                                                                    color: PRIMARY_ORANGE.main,
                                                                    backgroundColor: isDarkMode ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.08)',
                                                                }
                                                            }}
                                                        >
                                                            {document.linkUrl ? <LinkIcon /> : <DownloadIcon />}
                                                        </IconButton>
                                                    )}
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDelete(document.id, document.fileUrl)}
                                                        sx={{
                                                            color: '#ef4444',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(239, 68, 68, 0.15)',
                                                            }
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Box>
                    )}

                    {currentTab === 'upload' && (
                        <Box>
                            <Typography variant="h6" sx={{ color: theme.textPrimary, mb: 3 }}>
                                Upload New Document
                            </Typography>

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" sx={{ color: theme.textPrimary, mb: 2 }}>
                                    Document Type
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {(['cv', 'linkedin', 'additional'] as const).map((type) => (
                                    <Chip
                                        key={type}
                                        label={FILE_TYPE_LABELS[type]}
                                        variant={uploadType === type ? 'filled' : 'outlined'}
                                        onClick={() => {
                                            // Prevent switching if upload is in progress
                                            if (isUploading) return;
                                            setUploadType(type);
                                        }}
                                        disabled={isUploading}
                                        sx={{
                                            cursor: isUploading ? 'not-allowed' : 'pointer',
                                            color: uploadType === type ? '#ffffff' : theme.textPrimary,
                                            backgroundColor: uploadType === type ? PRIMARY_ORANGE.main : 'transparent',
                                            borderColor: uploadType === type ? PRIMARY_ORANGE.main : theme.borderColor,
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                            height: { xs: 28, sm: 32 },
                                            '&:hover': {
                                                backgroundColor: uploadType === type
                                                    ? PRIMARY_ORANGE.dark
                                                    : isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                                                borderColor: uploadType === type ? PRIMARY_ORANGE.dark : theme.textSecondary,
                                            },
                                            '&:disabled': {
                                                opacity: 0.5,
                                                cursor: 'not-allowed',
                                            }
                                        }}
                                    />
                                ))}
                            </Box>
                            
                            {/* Document limits information */}
                            <Box sx={{ 
                                mt: 2, 
                                p: 2, 
                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                                borderRadius: 2,
                                border: `1px solid ${theme.borderColor}`
                            }}>
                                <Typography variant="body2" sx={{ color: theme.textSecondary, mb: 1 }}>
                                    <strong>Current limits:</strong> CV ({cvCount}/1), LinkedIn ({linkedinCount}/1), Additional ({additionalCount}/{additionalDocumentsLimit})
                                </Typography>
                                {!isPro && (
                                    <Typography variant="body2" sx={{ color: PRIMARY_ORANGE.main, fontWeight: 500 }}>
                                        💡 Upgrade to Pro to upload up to 10 additional documents (instead of 3)
                                    </Typography>
                                )}
                                {uploadType === 'additional' && additionalCount >= additionalDocumentsLimit && (
                                    <Typography variant="body2" sx={{ color: '#ef4444', fontWeight: 500 }}>
                                        ⚠️ You&apos;ve reached the maximum limit for additional documents
                                    </Typography>
                                )}
                                {uploadType === 'cv' && cvCount >= 1 && (
                                    <Typography variant="body2" sx={{ color: '#ef4444', fontWeight: 500 }}>
                                        ⚠️ You already have a CV uploaded. Delete it first to upload a new one.
                                    </Typography>
                                )}
                                {uploadType === 'linkedin' && linkedinCount >= 1 && (
                                    <Typography variant="body2" sx={{ color: '#ef4444', fontWeight: 500 }}>
                                        ⚠️ You already have a LinkedIn profile added. Delete it first to add a new one.
                                    </Typography>
                                )}
                            </Box>
                            </Box>

                            <Divider sx={{ my: 3, borderColor: theme.borderColor }} />

                            {uploadType === 'linkedin' ? (
                                <Box>
                                    <TextField
                                        fullWidth
                                        label="LinkedIn Profile URL"
                                        placeholder="https://www.linkedin.com/in/your-profile"
                                        value={linkedinUrl}
                                        onChange={(e) => setLinkedinUrl(e.target.value)}
                                        sx={{
                                            mb: 3,
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
                                                '& fieldset': {
                                                    borderColor: theme.borderColor,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: theme.textSecondary,
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: PRIMARY_ORANGE.main,
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.textSecondary,
                                                '&.Mui-focused': {
                                                    color: PRIMARY_ORANGE.main,
                                                }
                                            },
                                            '& .MuiInputBase-input': {
                                                color: theme.textPrimary,
                                            },
                                            '& .MuiFormHelperText-root': {
                                                color: theme.textSecondary,
                                            }
                                        }}
                                        helperText="Enter your complete LinkedIn profile URL (supports special characters)"
                                    />
                                </Box>
                            ) : (
                                <Box>
                                    <input
                                        type="file"
                                        id="file-upload"
                                        style={{ display: 'none' }}
                                        onChange={handleFileSelect}
                                        accept={uploadType === 'cv' ? '.pdf,.doc,.docx' : '.pdf,.doc,.docx,.jpg,.jpeg,.png'}
                                    />
                                    <label htmlFor="file-upload">
                                        <Paper
                                            sx={{
                                                p: { xs: 3, sm: 4 },
                                                textAlign: 'center',
                                                border: `2px dashed ${theme.borderColor}`,
                                                borderRadius: 2,
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                                                '&:hover': {
                                                    borderColor: PRIMARY_ORANGE.main,
                                                    backgroundColor: isDarkMode
                                                        ? 'rgba(59, 130, 246, 0.08)'
                                                        : 'rgba(59, 130, 246, 0.02)',
                                                }
                                            }}
                                        >
                                            <UploadIcon sx={{
                                                fontSize: { xs: 40, sm: 48 },
                                                color: selectedFile ? PRIMARY_ORANGE.main : theme.textSecondary,
                                                mb: 2
                                            }} />
                                            <Typography variant="h6" sx={{
                                                color: selectedFile ? PRIMARY_ORANGE.main : theme.textPrimary,
                                                mb: 1,
                                                fontWeight: selectedFile ? 600 : 400,
                                                fontSize: { xs: '1.1rem', sm: '1.25rem' }
                                            }}>
                                                {selectedFile ? selectedFile.name : 'Choose file to upload'}
                                            </Typography>
                                            <Typography variant="body2" sx={{ 
                                                color: theme.textSecondary,
                                                fontSize: { xs: '0.875rem', sm: '1rem' }
                                            }}>
                                                {uploadType === 'cv'
                                                    ? 'Supported formats: PDF, DOC, DOCX (Max 10MB)'
                                                    : 'Supported formats: PDF, DOC, DOCX, JPG, PNG (Max 10MB)'
                                                }
                                            </Typography>
                                        </Paper>
                                    </label>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{
                px: { xs: 2, sm: 3 },
                py: 2,
                borderTop: `1px solid ${theme.borderColor}`,
                background: isDarkMode ? '#2d2d2d' : '#ffffff',
                flexDirection: { xs: 'column-reverse', sm: 'row' },
                gap: { xs: 1, sm: 0 },
            }}>
                <Button
                    onClick={onClose}
                    sx={{
                        color: theme.textSecondary,
                        textTransform: 'none',
                        width: { xs: '100%', sm: 'auto' },
                        '&:hover': {
                            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                            color: theme.textPrimary,
                        }
                    }}
                >
                    Close
                </Button>
                {currentTab === 'upload' && (
                    <Button
                        variant="contained"
                        startIcon={isUploading ? <CircularProgress size={16} sx={{ color: '#ffffff' }} /> : <UploadIcon />}
                        onClick={handleUpload}
                        disabled={
                            isUploading || 
                            (uploadType !== 'linkedin' && !selectedFile) || 
                            (uploadType === 'linkedin' && !linkedinUrl.trim()) ||
                            (uploadType === 'additional' && additionalCount >= additionalDocumentsLimit) ||
                            (uploadType === 'cv' && cvCount >= 1) ||
                            (uploadType === 'linkedin' && linkedinCount >= 1)
                        }
                        sx={{
                            backgroundColor: PRIMARY_ORANGE.main,
                            color: '#ffffff',
                            fontWeight: 600,
                            textTransform: 'none',
                            width: { xs: '100%', sm: 'auto' },
                            '&:hover': {
                                backgroundColor: PRIMARY_ORANGE.dark,
                            },
                            '&:disabled': {
                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                                color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
                            }
                        }}
                    >
                        {isUploading ? 'Uploading...' : 'Upload'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default DocumentManagerDialog; 